<template>
  <div class="settings-page integration">
    <div class="title">
      <div>
        <NotionIcon class="header-icon" />
        <h2>Notion (deprecated)</h2>
      </div>
      <div class="actions">
        <BaseLink
          @click="openUrl('https://routine.co/integrations/services/notion')"
        >
          <template #icon> <ExternalIcon /> </template>Help
        </BaseLink>
        <BaseButton
          v-if="inElectron"
          kind="main"
          type="secondary"
          text="Refresh"
          @click="refresh()"
        >
          <template #icon>
            <RefreshIcon />
          </template>
        </BaseButton>
      </div>
    </div>

    <p class="description">
      Synchronize tasks from your Notion databases. This integration is
      deprecated and will be removed: set up Notion again from the integrations
      list to move to its replacement.
    </p>

    <div v-if="!inElectron">
      <DownloadButton
        kind="main"
        type="secondary"
        text="Enable"
        message="The Notion integration is only supported in the Desktop apps."
      />
    </div>
    <section v-else>
      <!-- <div v-if="!integrations || integrations.length == 0"> -->
      <div v-if="false">
        <div class="entry">
          <label>Connect a workspace</label>
          <BaseButton
            kind="main"
            type="secondary"
            text="Connect"
            @click="addAccount"
          />
        </div>
      </div>

      <div v-else>
        <div class="entry">
          <label class="sub-header">Workspaces</label>
          <BaseButton
            v-if="integrations.length === 0"
            kind="main"
            type="secondary"
            text="Connect"
            @click="addAccount"
          />
          <BaseButton
            v-else
            kind="main"
            type="secondary"
            text="Connect"
            @click="addAccount"
          />
        </div>

        <!-- LIST ALL CONNECTIONS -->
        <div
          v-for="[id, integration] in integrations"
          :key="id"
          class="connections"
        >
          <div class="entry">
            <div>
              <Spinner v-if="integration.status?.status === 'synchronizing'" />
              <label v-if="integration.status?.status === 'ready'">
                {{ getIntegrationUserMail(integration) }}
              </label>
              <label v-else-if="integration.status?.status === 'broken'">
                {{ getIntegrationError(integration) }}
              </label>
              <label v-else />
            </div>

            <div class="actions">
              <BaseLink
                v-if="getIntegrationError(integration)"
                @click="addAccount"
              >
                Retry to connect
              </BaseLink>
              <BaseLink @click="removeIntegration(id)"> Disconnect </BaseLink>
            </div>
          </div>

          <!-- LIST ALL IMPORTS -->
          <div class="databases">
            <div v-for="data in integration.imports" :key="data.statusColumnId">
              <div class="entry small">
                <div>
                  <span class="icon">⌙</span>
                  <span class="description primary">{{
                    getDatabaseName(integration, data)
                  }}</span>
                </div>

                <Spinner
                  v-if="integration.status?.status === 'synchronizing'"
                  class="loader"
                />
                <BaseLink
                  v-else-if="
                    integration.status?.status === 'ready' ||
                    integration.status?.status === 'broken'
                  "
                  class="edit"
                  @click="editDatabase(id, data.databaseId)"
                >
                  Edit
                </BaseLink>
              </div>

              <div
                v-if="integration.status?.status === 'broken'"
                class="entry small"
              >
                <label>There was an error.</label>
                {{ integration.status?.error }}
              </div>
            </div>
          </div>

          <p
            v-if="!integration.imports || integration.imports.length == 0"
            class="description"
          >
            Add the databases from which tasks should be imported and
            synchronized.
          </p>

          <BaseButton
            kind="main"
            text="Add Database"
            type="secondary"
            @click="addDatabase(id)"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import type {
  IntegrationReport,
  // Integrations,
  NotionConfig,
  NotionDatabase,
  NotionImport,
  NotionPeople,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseLink from "@/components/base/BaseLink.vue";
import DownloadButton from "@/components/base/DownloadButton.vue";
import NotionIcon from "@/components/icons/apps/NotionIcon.vue";
import ExternalIcon from "@/components/icons/ExternalIcon.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import * as inject from "@/components/inject";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { in_electron } from "@/utils/in";
import { navigateTo } from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";

import { notifyCheckBrowser } from "../utils";

export default defineComponent({
  components: {
    BaseButton,
    BaseLink,
    DownloadButton,
    NotionIcon,
    ExternalIcon,
    RefreshIcon,
  },
  props: {
    orgId: { type: String, required: true },
  },
  async setup(props) {
    const controller = inject.controller();
    const router = inject.router();
    const releaser = useReleaser();
    const integrations = await releaser.add(
      controller.integration
        .list()
        .then((list) =>
          list.map((integrations) =>
            Object.entries(integrations).filter(
              (
                integration
              ): integration is [
                string,
                NotionConfig<IntegrationReport> & { type: "notion" },
              ] => integration[1].type === "notion"
            )
          )
        )
    );

    function getIntegrationError(
      integration: NotionConfig<IntegrationReport>
    ): string | null {
      return (
        (integration.status?.status === "broken" &&
          integration.status?.error) ||
        null
      );
    }

    function getIntegrationUserMail(
      integration: NotionConfig<IntegrationReport>
    ): string {
      if (integration.status?.status !== "ready") {
        return "␦";
      }
      const integrationUser = integration?.people?.find(
        (people: NotionPeople) => people.id === integration.self
      );
      return integrationUser ? integrationUser.email : "";
    }

    function getDatabaseName(
      integration: NotionConfig<IntegrationReport>,
      notionImport: NotionImport
    ): string {
      if (integration.status?.status !== "ready") {
        if (integration.status?.status === "broken") {
          return "broken";
        }
        return "␦";
      }
      const selectedDatabase = integration?.databases?.find(
        (db: NotionDatabase) => db.id === notionImport.databaseId
      );
      return selectedDatabase?.title || "␦";
    }

    async function addAccount() {
      const { api } = await controller.channel();
      const source = in_electron ? "app" : "web";
      notifyCheckBrowser(controller);
      openExternalUrl(
        `${api}/integrations/notion/oauth?source=${source}`,
        controller
      );
      // This will return to routine://./settings, see Settings.vue
    }

    function removeIntegration(id: string) {
      controller.integration.remove(id);
    }

    function addDatabase(integrationId: string) {
      if (integrationId) {
        navigateTo(RouteNames.SettingsIntegrationsNotionSetup, router, {
          params: { orgId: props.orgId, id: integrationId },
        });
      }
    }

    function editDatabase(integrationId: string, id: string) {
      if (integrationId && id) {
        navigateTo(RouteNames.SettingsIntegrationsNotionSetup, router, {
          params: { orgId: props.orgId, id: integrationId },
          query: {
            db_id: id,
          },
        });
      }
    }

    function refresh() {
      integrations.value.forEach(([id, _]) => {
        controller.integration.refresh(id);
      });
    }
    const openUrl = (url: string) => openExternalUrl(url, controller);
    return {
      addAccount,
      addDatabase,
      editDatabase,
      getDatabaseName,
      getIntegrationError,
      getIntegrationUserMail,
      inElectron: in_electron,
      integrations,
      openExternalUrl,
      openUrl,
      refresh,
      removeIntegration,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  h2 {
    margin-bottom: 0;
  }

  .actions {
    display: flex;
    gap: 8px;
  }
}

.header-icon {
  height: 32px;
  width: 32px;
}

.connections {
  margin-bottom: 32px;
}
</style>
